import * as amplitudeEngagement from '@amplitude/engagement-browser';
import type { Types } from '@amplitude/analytics-browser';
import type { BootOptions, EngagementSDK } from '@amplitude/engagement-browser';
import { getAmplitudeEngagementLanguage } from '../../common/analytics/amplitudeLocalizationUtils';
import globalLogger from '../logger/logger';

type SyncEngagementArgs = {
    isEnabled: boolean;
    isGuest: boolean;
    userId: string | null;
    locale: string;
    config: {
        analytics?: {
            amplitudeApiKey?: string;
        };
    };
    userProperties?: Record<string, unknown>;
};

const engagementState = {
    isEnabled: false,
    currentLocale: '',
    currentUserId: undefined as string | null | undefined,
    currentUserProperties: undefined as Record<string, unknown> | undefined,
};

const logger = globalLogger.createChannel('engagement-service');
const ENGAGEMENT_PLUGIN_CALLER = Symbol.for('amplitude-engagement-plugin-caller');
let analyticsClient: Types.BrowserClient | undefined;
let analyticsConfig: Types.BrowserConfig | undefined;
let engagementUserId: string | undefined;
let appliedLocale = '';
let engagementUpdate = Promise.resolve();

type PluginManagedEngagement = EngagementSDK & {
    boot(options: BootOptions, caller: symbol): Promise<void>;
    shutdown(caller: symbol): void;
};

type EngagementCallback = (...args: unknown[]) => void;

const engagementCallbacks = new Map<string, EngagementCallback>();

const applyEngagementCallbacks = () =>
    engagementCallbacks.forEach((callback, key) => window.engagement?.addCallback(key, callback));

/** Applies the latest consent, locale and identity in one serial SDK update. */
const applyLatestEngagementState = async (): Promise<void> => {
    if (!window.engagement) return;
    const engagement = window.engagement as PluginManagedEngagement;
    if (!engagementState.isEnabled) {
        engagement.disable();
        return;
    }

    engagement.enable();
    const locale = engagementState.currentLocale;
    if (locale && locale !== appliedLocale) {
        await engagement.updateLanguage(locale);
        appliedLocale = locale;
    }
    applyEngagementCallbacks();

    if (!engagementState.isEnabled) {
        engagement.disable();
        return;
    }
    if (!analyticsClient || !analyticsConfig) return;

    const client = analyticsClient;
    const config = analyticsConfig;
    const identity = client.getIdentity();
    const expectedUserId = engagementState.currentUserId ?? undefined;
    if (identity.userId !== expectedUserId || engagementUserId === expectedUserId) return;
    const userProperties = { ...identity.userProperties, ...engagementState.currentUserProperties };

    engagement.shutdown(ENGAGEMENT_PLUGIN_CALLER);
    await engagement.boot(
        {
            user: () => ({
                user_id: identity.userId,
                device_id: identity.deviceId,
                version: config.appVersion,
                user_properties: userProperties,
                getSessionId: () => client.getSessionId(),
            }),
            integrations: [{ track: (event) => client.track(event) }],
        },
        ENGAGEMENT_PLUGIN_CALLER,
    );

    if (!engagementState.isEnabled) {
        engagement.disable();
        return;
    }

    if ((engagementState.currentUserId ?? undefined) !== identity.userId || client.getUserId() !== identity.userId) {
        return;
    }

    engagementUserId = identity.userId;
    applyEngagementCallbacks();
};

/** Queues all Engagement mutations so stale async work cannot finish last. */
const queueEngagementUpdate = (): Promise<void> => {
    engagementUpdate = engagementUpdate.catch(() => {}).then(applyLatestEngagementState);
    return engagementUpdate;
};

/** Guide CTA callbacks, keyed the way the Guide's "on button click" code action addresses them. */
export const addEngagementCallback = (key: string, callback: EngagementCallback): void => {
    engagementCallbacks.set(key, callback);
    window.engagement?.addCallback(key, callback);
};

/**
 * Creates a fail-open plugin so Engagement cannot interrupt Analytics delivery.
 */
export const createEngagementPlugin = (): Types.EnrichmentPlugin<Types.BrowserClient, Types.BrowserConfig> => {
    const plugin = amplitudeEngagement.plugin({
        locale: engagementState.currentLocale || undefined,
        autoRefreshIntervalSeconds: 3600,
    }) as Types.EnrichmentPlugin<Types.BrowserClient, Types.BrowserConfig>;
    const setup = plugin.setup?.bind(plugin);
    const execute = plugin.execute?.bind(plugin);

    if (setup) {
        plugin.setup = async (config, client) => {
            const currentUserId = engagementState.currentUserId ?? undefined;
            if (!client.getUserId() && currentUserId) {
                const userProperties = {
                    ...client.getIdentity().userProperties,
                    ...engagementState.currentUserProperties,
                };
                client.setIdentity({ userId: currentUserId, userProperties });
            }

            const identityBeforeSetup = client.getIdentity();
            await setup(config, client);
            analyticsClient = client;
            analyticsConfig = config;
            engagementUserId = identityBeforeSetup.userId;

            await queueEngagementUpdate();
        };
    }

    if (!execute) return plugin;

    plugin.execute = async (event) => {
        try {
            return await execute(event);
        } catch (error: unknown) {
            // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
            logger.warn('Failed to forward Amplitude event to Engagement', error);
            return event;
        }
    };

    return plugin;
};

/** Applies consent, locale and callbacks after the plugin boots. */
export const applyEngagementState = queueEngagementUpdate;

export const syncEngagementTracking = async ({
    isEnabled,
    isGuest,
    userId,
    locale,
    userProperties,
}: SyncEngagementArgs): Promise<void> => {
    engagementState.isEnabled = isEnabled;
    engagementState.currentLocale = getAmplitudeEngagementLanguage(locale);
    engagementState.currentUserId = isGuest ? null : userId;
    engagementState.currentUserProperties = isGuest ? undefined : userProperties;
    await queueEngagementUpdate();
};
